import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { useUserAgents } from "@/hooks/agent/useAgents";
import type { Agent } from "@/types/agent/agent";
import { GenieMiniAvatar } from "./GenieAttribution";

interface Props {
  selected: string[] | null;          // null or [] = all genies
  onChange: (next: string[] | null) => void;
  disabled?: boolean;
}

// Multi-select genie scope picker. The trigger surfaces actual Genie avatars
// (stacked when multiple are selected) so customers always see at a glance
// *which* Genies their view will analyse before they type.
export const GenieScopePicker = ({ selected, onChange, disabled }: Props) => {
  const { data: agents = [], isLoading } = useUserAgents(100);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selectedSet = new Set(selected ?? []);
  const allSelected = !selected || selected.length === 0;
  const selectedAgents = useMemo(
    () => agents.filter((a) => selectedSet.has(a.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [agents, selected],
  );

  const filteredAgents = useMemo(() => {
    if (!query.trim()) return agents;
    const q = query.trim().toLowerCase();
    return agents.filter((a) => a.name?.toLowerCase().includes(q));
  }, [agents, query]);

  const toggle = (agentId: string) => {
    const next = new Set(selectedSet);
    if (next.has(agentId)) next.delete(agentId);
    else next.add(agentId);
    onChange(next.size === 0 ? null : Array.from(next));
  };

  const triggerLabel = allSelected
    ? "All of your Genies"
    : selectedAgents.length === 1
      ? selectedAgents[0].name
      : `${selectedAgents.length} Genies selected`;

  const triggerAccent = !allSelected && selectedAgents.length === 1
    ? selectedAgents[0].branding?.primaryColor ?? "#4E9CFF"
    : null;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full text-sm font-medium border bg-white text-[#222B45] hover:shadow-sm transition-all disabled:opacity-60"
        style={{
          borderColor: triggerAccent ? `${triggerAccent}55` : "#E5EAF0",
        }}
        aria-label={`Showing: ${triggerLabel}. Click to change.`}
      >
        <span className="text-[#64748B] text-xs">Showing:</span>
        <span className="inline-flex items-center">
          {allSelected ? (
            <span
              className="inline-flex items-center justify-center rounded-full text-[9px] font-bold text-white ring-2 ring-white"
              style={{ width: 22, height: 22, background: "linear-gradient(135deg, #4E9CFF, #FF4DB8)" }}
              aria-hidden
            >
              All
            </span>
          ) : (
            <>
              {selectedAgents.slice(0, 3).map((a, i) => (
                <span key={a.id} style={{ marginLeft: i === 0 ? 0 : -8, zIndex: 3 - i }}>
                  <GenieMiniAvatar agent={a} size={22} />
                </span>
              ))}
              {selectedAgents.length > 3 && (
                <span
                  className="inline-flex items-center justify-center rounded-full bg-[#FAFCFF] border border-[#E5EAF0] text-[10px] font-semibold text-[#64748B] ring-2 ring-white"
                  style={{ width: 22, height: 22, marginLeft: -8 }}
                >
                  +{selectedAgents.length - 3}
                </span>
              )}
            </>
          )}
        </span>
        <span className="font-semibold truncate max-w-[200px]" style={{ color: triggerAccent ?? "#222B45" }}>
          {triggerLabel}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-[#64748B] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Inline chips for 2+ selected Genies — lets the user deselect without
          reopening the popover. */}
      {selectedAgents.length >= 2 && (
        <div className="inline-flex flex-wrap gap-1.5 ml-2 align-middle">
          {selectedAgents.map((a) => (
            <button
              key={a.id}
              onClick={() => toggle(a.id)}
              disabled={disabled}
              className="inline-flex items-center gap-1 pl-0.5 pr-2 py-0.5 rounded-full text-[11px] font-medium bg-white border border-[#E5EAF0] text-[#222B45] hover:border-genie-primary/40 transition-colors"
            >
              <GenieMiniAvatar agent={a} size={16} />
              <span className="truncate max-w-[140px]">{a.name}</span>
              <X className="h-3 w-3 text-[#B6C0CC]" aria-hidden />
            </button>
          ))}
        </div>
      )}

      {open && (
        <div
          className="absolute z-30 mt-2 w-[min(360px,calc(100vw-2rem))] bg-white border border-[#E5EAF0] rounded-xl shadow-xl overflow-hidden"
          style={{ boxShadow: "0 12px 32px -12px rgba(15, 23, 42, .25)" }}
        >
          <div className="px-3 py-2.5 border-b border-[#E5EAF0] flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#64748B]">Pick the Genies to analyze</span>
              <span className="text-[11px] text-[#64748B] mt-0.5">Leave empty to include every Genie.</span>
            </div>
            {!allSelected && (
              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-[11px] font-semibold text-genie-primary hover:opacity-80"
              >
                Clear
              </button>
            )}
          </div>

          {agents.length > 5 && (
            <div className="px-3 pt-2">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search Genies..."
                className="w-full text-sm bg-[#FAFCFF] border border-[#E5EAF0] rounded-lg px-3 py-1.5 outline-none focus:border-genie-primary"
              />
            </div>
          )}

          <div className="max-h-80 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => onChange(null)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-[#FAFCFF] transition-colors ${allSelected ? "bg-[#FAFCFF]" : ""}`}
            >
              <span
                className="inline-flex items-center justify-center rounded-full text-[9px] font-bold text-white shrink-0"
                style={{ width: 28, height: 28, background: "linear-gradient(135deg, #4E9CFF, #FF4DB8)" }}
              >
                All
              </span>
              <div className="flex-1 min-w-0">
                <div className={`font-semibold ${allSelected ? "text-genie-primary" : "text-[#222B45]"}`}>All of your Genies</div>
                <div className="text-[11px] text-[#64748B] truncate">{agents.length} {agents.length === 1 ? "Genie" : "Genies"} total · analyse everything</div>
              </div>
              {allSelected && <Check className="h-4 w-4 text-genie-primary shrink-0" strokeWidth={3} />}
            </button>

            <div className="h-px bg-[#E5EAF0] my-1" />

            {isLoading && <div className="px-3 py-3 text-xs text-[#64748B]">Loading your Genies...</div>}
            {!isLoading && agents.length === 0 && (
              <div className="px-3 py-4 text-xs text-[#64748B]">No Genies yet. Create one to start filtering.</div>
            )}
            {!isLoading && filteredAgents.length === 0 && agents.length > 0 && (
              <div className="px-3 py-4 text-xs text-[#64748B]">No Genies match "{query}".</div>
            )}
            {filteredAgents.map((a) => {
              const isOn = selectedSet.has(a.id);
              return (
                <AgentRow
                  key={a.id}
                  agent={a}
                  selected={isOn}
                  onToggle={() => toggle(a.id)}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

interface AgentRowProps {
  agent: Agent;
  selected: boolean;
  onToggle: () => void;
}

const AgentRow = ({ agent, selected, onToggle }: AgentRowProps) => {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-[#FAFCFF] transition-colors ${selected ? "bg-[#FAFCFF]" : ""}`}
    >
      <GenieMiniAvatar agent={agent} size={28} />
      <div className="flex-1 min-w-0">
        <div className={`font-semibold truncate ${selected ? "text-genie-primary" : "text-[#222B45]"}`}>{agent.name}</div>
        {agent.description && (
          <div className="text-[11px] text-[#64748B] truncate">{agent.description}</div>
        )}
      </div>
      <span className={`h-4 w-4 rounded-md border flex items-center justify-center shrink-0 ${selected ? "bg-genie-primary border-genie-primary" : "border-[#E5EAF0]"}`}>
        {selected ? <Check className="h-3 w-3 text-white" strokeWidth={3} /> : null}
      </span>
    </button>
  );
};
